
import { useMemo } from 'react';

interface ReviewReminderProps {
  totalCount: number;
  subjectBreakdown: Record<string, number>;
  onStartReview: () => void;
  layoutMode?: 'normal' | 'compact';
}

const subjectColors: Record<string, string> = {
  '研究方法論': 'from-blue-500 to-cyan-500',
  '統計学': 'from-purple-500 to-pink-500',
  '心理学理論': 'from-green-500 to-emerald-500',
  '実験心理学': 'from-orange-500 to-red-500',
  '発達心理学': 'from-indigo-500 to-purple-500',
  '社会心理学': 'from-teal-500 to-cyan-500',
  '認知心理学': 'from-amber-500 to-orange-500',
  '臨床心理学': 'from-rose-500 to-pink-500',
};

const subjectIcons: Record<string, string> = {
  '研究方法論': 'ri-microscope-line',
  '統計学': 'ri-bar-chart-line',
  '心理学理論': 'ri-brain-line',
  '実験心理学': 'ri-flask-line',
  '発達心理学': 'ri-seedling-line',
  '社会心理学': 'ri-group-line',
  '認知心理学': 'ri-lightbulb-line',
  '臨床心理学': 'ri-heart-pulse-line',
};

/**
 * 今日の復習おすすめカード
 */
export default function ReviewReminder({
  totalCount,
  subjectBreakdown,
  onStartReview,
  layoutMode = 'normal',
}: ReviewReminderProps) {
  const sortedSubjects = useMemo(() => {
    return Object.entries(subjectBreakdown ?? {}).sort((a, b) => b[1] - a[1]);
  }, [subjectBreakdown]);

  if (totalCount === 0) {
    return null;
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${layoutMode === 'compact' ? 'p-4' : 'p-6'}`}>
      <h3 className={`font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2 ${layoutMode === 'compact' ? 'text-base' : 'text-lg'}`}>
        <i className="ri-alarm-line text-orange-600"></i>
        復習リマインダー
        <span className="ml-auto text-sm font-normal text-orange-500 bg-orange-50 dark:bg-orange-900/30 px-2 py-0.5 rounded-full">
          {totalCount}件
        </span>
      </h3>

      <div className={`space-y-3 ${layoutMode === 'compact' ? 'text-sm' : ''}`}>
        {sortedSubjects.length > 0 ? (
          sortedSubjects.map(([subject, count]) => {
            const icon = subjectIcons[subject] ?? 'ri-book-line';
            return (
              <div
                key={subject}
                className={`flex items-center gap-3 ${layoutMode === 'compact' ? 'p-2' : 'p-3'} bg-orange-50 dark:bg-orange-900/20 rounded-lg`}
              >
                <div className={`bg-orange-100 dark:bg-orange-800/40 text-orange-600 dark:text-orange-400 rounded-lg flex items-center justify-center flex-shrink-0 ${layoutMode === 'compact' ? 'w-8 h-8 text-base' : 'w-10 h-10 text-lg'}`}>
                  <i className={icon}></i>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800 dark:text-white">{subject}</p>
                  <p className={`text-gray-500 dark:text-gray-400 ${layoutMode === 'compact' ? 'text-xs' : 'text-sm'}`}>
                    {count}問 復習推奨
                  </p>
                </div>
                <span className="text-orange-600 dark:text-orange-400 font-medium px-2 py-0.5 text-xs bg-orange-100 dark:bg-orange-800/40 rounded-full">
                  要復習
                </span>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <i className="ri-alarm-line text-4xl text-gray-300 dark:text-gray-600 mb-2"></i>
            <p className="text-gray-500 dark:text-gray-400">復習予定はありません</p>
          </div>
        )}
      </div>

      {sortedSubjects.length > 0 && (
        <button
          onClick={onStartReview}
          className={`mt-4 w-full bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors ${layoutMode === 'compact' ? 'py-2 text-sm' : 'py-2.5 text-base'}`}
        >
          <i className="ri-play-circle-line mr-2"></i>
          復習を始める
        </button>
      )}
    </div>
  );
}
